import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { T_STRING, T_NUMBER, T_BOOLEAN, recordOf, arrayOf } from "@features/flows/utils/types";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";

type Props = {
  valueType?: "string" | "number" | "boolean";
  applyInference?: boolean;
};

function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  map.set("core.object.values", {
    key: "core.object.values",
    title: "Object • Values",
    category: "Object",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "object", type: "object" }], out: [{ id: "values", type: "array" }] },
    },
    pkg: "core",
    typeLinks: [
      { from: { io: "in", id: "object", op: ["value", "arrayOf"] }, to: { io: "out", id: "values" } },
    ],
  } as any);
  return map;
}

function ObjectValuesPreview({ valueType = "string", applyInference = true }: Props) {
  const templates = React.useMemo(buildTemplates, []);
  const valueExpr = valueType === "number" ? T_NUMBER : valueType === "boolean" ? T_BOOLEAN : T_STRING;
  const recordExpr = recordOf(T_STRING, valueExpr);
  const baseNodes: Node[] = [
    {
      id: "src",
      type: "default",
      position: { x: 80, y: 120 },
      data: {
        label: `Record<string, ${valueType}>`,
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [], out: [{ id: "object", type: "object", typeExpr: recordExpr }] },
        },
      },
    },
    {
      id: "vals",
      type: "default",
      position: { x: 420, y: 120 },
      data: {
        label: "Object • Values",
        templateKey: "core.object.values",
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [{ id: "object", type: "object" }], out: [{ id: "values", type: "array" }] },
        },
      },
    },
  ];
  const baseEdges: Edge[] = [
    {
      id: "e-obj",
      type: "data",
      source: "src",
      target: "vals",
      sourceHandle: "data:out:object",
      targetHandle: "data:in:object",
      data: { channel: "data", rawSourceHandle: "object", rawTargetHandle: "object" },
    } as any,
  ];
  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edges, setEdges] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    if (!applyInference) { setNodes(baseNodes); setEdges(baseEdges); return; }
    try {
      const inferred = inferTypes(baseNodes as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(baseNodes as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdges(baseEdges);
    } catch {
      setNodes(baseNodes);
      setEdges(baseEdges);
    }
  }, [applyInference, valueType, templates]);

  return (
    <div className="h-[560px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/ObjectValues",
  title: "Object • Values",
  group: "Flows/Typing",
  component: ObjectValuesPreview as any,
  description: "Infers values: arrayOf(value(object))",
  defaultProps: { valueType: "string", applyInference: true },
  controls: {
    valueType: { type: "select", label: "Value Type", options: ["string", "number", "boolean"] },
    applyInference: { type: "boolean", label: "Apply Inference" },
  },
  variants: [
    { name: "Before (no inference)", props: { applyInference: false } },
    { name: "After (string)", props: { valueType: "string", applyInference: true } },
    { name: "After (number)", props: { valueType: "number", applyInference: true } },
  ],
};

export default meta;

